'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Eye, EyeOff, RefreshCw, Sparkles } from 'lucide-react'
import Image from 'next/image'
import platformLogo from '@/public/logo.png'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useAuth } from '@/lib/auth-context'
import Api from '@/api/h5Api'

interface ApiResponse<T = any> {
  code: number
  message?: string
  data: T
}

interface LoginDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onRegisterClick?: () => void
  onForgotPasswordClick?: () => void
}

export function LoginDialog({
  open,
  onOpenChange,
  onRegisterClick,
  onForgotPasswordClick,
}: LoginDialogProps) {
  const router = useRouter()
  const { setAuthUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)

  // 表单状态
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [captchaId, setCaptchaId] = useState('')
  const [captcha, setCaptcha] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // 调试模式
  const [debugCount, setDebugCount] = useState(0)
  const [isDebugMode, setIsDebugMode] = useState(false)
  const [debugPassword, setDebugPassword] = useState('')

  // 获取验证码
  const getCaptcha = async () => {
    try {
      const res = (await Api.userCaptcha({})) as unknown as ApiResponse
      if (res.code === 200) {
        setCaptcha(res.data.captcha)
        setCaptchaId(res.data.captchaId)
      }
    } catch (error) {
      console.error('获取验证码失败:', error)
    }
  }

  // 处理登录
  const handleLogin = async () => {
    setLoginError(null)

    if (username.trim() === '') {
      setLoginError('用户名不能为空')
      return
    }
    if (password.trim() === '') {
      setLoginError('密码不能为空')
      return
    }
    if (code.trim() === '') {
      setLoginError('验证码不能为空')
      return
    }

    setIsLoading(true)
    try {
      const res = (await Api.userAiLogin(
        {
          username,
          password,
          code,
          captchaId,
          isDebuger: isDebugMode,
          debugerPassword: debugPassword,
        },
        true
      )) as unknown as ApiResponse

      if (res.code === 200) {
        // 存储用户信息到 localStorage
        if (typeof window !== 'undefined') {
          localStorage.setItem('username', res.data.username)
          localStorage.setItem('nickname', res.data.nickname)
          localStorage.setItem('token', res.data.token)
          localStorage.setItem('talkId', res.data.talkId)
          localStorage.setItem('uid', res.data.uid)
        }

        // 设置认证用户信息
        setAuthUser(res.data)

        // 关闭对话框
        onOpenChange(false)

        // 刷新页面
        router.refresh()
      }
    } catch (error: any) {
      console.error('登录失败:', error)
      setLoginError(error?.message || '登录失败，请稍后重试')
      getCaptcha() // 重新获取验证码
    } finally {
      setIsLoading(false)
    }
  }

  // 游客登录
  const handleGuestLogin = async () => {
    setIsLoading(true)
    setLoginError(null)

    try {
      const guestRes = (await Api.userAiGuestAdd(
        {},
        false
      )) as unknown as ApiResponse
      if (guestRes.code === 200) {
        const { username, password } = guestRes.data
        const loginRes = (await Api.userAiLogin(
          {
            username,
            password,
            isGuest: true,
            isDebuger: isDebugMode,
            debugerPassword: debugPassword,
          },
          false
        )) as unknown as ApiResponse

        if (loginRes.code === 200) {
          if (typeof window !== 'undefined') {
            localStorage.setItem('username', loginRes.data.username)
            localStorage.setItem('nickname', loginRes.data.nickname)
            localStorage.setItem('token', loginRes.data.token)
            localStorage.setItem('talkId', loginRes.data.talkId)
            localStorage.setItem('uid', loginRes.data.uid)
          }

          setAuthUser(loginRes.data)
          onOpenChange(false)
          router.refresh()
        }
      }
    } catch (error: any) {
      console.error('游客登录失败:', error)
      setLoginError(error?.message || '游客登录失败，请稍后重试')
    } finally {
      setIsLoading(false)
    }
  }

  // 处理调试模式
  const handleDebugClick = () => {
    setDebugCount((prev) => prev + 1)
    if (debugCount >= 5) {
      setIsDebugMode(true)
      if (typeof window !== 'undefined') {
        localStorage.setItem('isDebuger', '1')
      }
    }
  }

  // Enter 键登录
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleLogin()
    }
  }

  // 当对话框关闭时重置表单
  useEffect(() => {
    if (!open) {
      setUsername('')
      setPassword('')
      setCode('')
      setShowPassword(false)
      setLoginError(null)
      setDebugPassword('')
    } else {
      // 对话框打开时获取验证码
      getCaptcha()
    }
  }, [open])

  // 检查是否已经是调试模式
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedDebugMode = localStorage.getItem('isDebuger')
      if (savedDebugMode === '1') {
        setIsDebugMode(true)
      }
    }
  }, [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div
                className="relative w-10 h-10 rounded-xl overflow-hidden cursor-pointer"
                onClick={handleDebugClick}
              >
                <Image
                  src={platformLogo}
                  alt="AI智能学习平台"
                  fill
                  className="object-contain"
                />
              </div>
              <h1 className="text-lg font-bold text-primary">AI智能学习平台</h1>
            </div>
            登录您的账号
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Error Alert */}
          {loginError && (
            <Alert variant="destructive">
              <AlertDescription>{loginError}</AlertDescription>
            </Alert>
          )}

          {/* Username Input */}
          <div className="space-y-2">
            <Label htmlFor="username" className="text-sm font-medium">
              邮箱/账号
            </Label>
            <Input
              id="username"
              type="text"
              placeholder="请输入邮箱或账号"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={handleKeyDown}
              className="focus:ring-primary focus:border-primary"
              autoFocus
            />
          </div>

          {/* Password Input */}
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium">
              密码
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="请输入密码"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                className="focus:ring-primary focus:border-primary pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-300"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Captcha Input */}
          <div className="space-y-2">
            <Label htmlFor="code" className="text-sm font-medium">
              验证码
            </Label>
            <div className="flex gap-2">
              <Input
                id="code"
                type="text"
                placeholder="请输入验证码"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={6}
                className="focus:ring-primary focus:border-primary flex-1"
              />
              <button
                onClick={getCaptcha}
                className="flex-shrink-0 w-28 h-10 rounded-lg bg-muted hover:bg-muted/80 border border-border transition-all duration-300 overflow-hidden flex items-center justify-center"
                dangerouslySetInnerHTML={{ __html: captcha }}
              />
            </div>
          </div>

          {/* Debug Mode */}
          {isDebugMode && (
            <>
              <div className="space-y-2">
                <Label htmlFor="debugPassword" className="text-sm font-medium">
                  调试密码
                </Label>
                <Input
                  id="debugPassword"
                  type="password"
                  placeholder="请输入调试密码"
                  value={debugPassword}
                  onChange={(e) => setDebugPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="focus:ring-primary focus:border-primary"
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Sparkles className="w-3 h-3" />
                <span>调试模式已开启</span>
                <button
                  onClick={() => {
                    setIsDebugMode(false)
                    setDebugCount(0)
                    if (typeof window !== 'undefined') {
                      localStorage.setItem('isDebuger', '2')
                    }
                  }}
                  className="ml-auto text-primary hover:underline"
                >
                  关闭
                </button>
              </div>
            </>
          )}

          {/* Login Button */}
          <Button
            onClick={handleLogin}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" />
                登录中...
              </span>
            ) : (
              '登录'
            )}
          </Button>

          {/* Guest Login Button */}
          <Button
            onClick={handleGuestLogin}
            variant="outline"
            className="w-full"
            disabled={isLoading}
          >
            游客登录
          </Button>

          {/* Footer Links */}
          <div className="flex items-center justify-center gap-3 pt-2 text-sm border-t">
            <button
              className="text-muted-foreground hover:text-primary transition-colors"
              onClick={() => {
                onOpenChange(false)
                onRegisterClick?.()
              }}
            >
              注册账号
            </button>
            <span className="text-muted-foreground">•</span>
            <button
              className="text-muted-foreground hover:text-primary transition-colors"
              onClick={() => {
                onOpenChange(false)
                onForgotPasswordClick?.()
              }}
            >
              忘记密码
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
